import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useIntl } from 'react-intl';

import CardSelectionController from 'src/components/Dashboard/CardSelectionController';
import {
  buildChartViewKey,
  getCardSelectionState,
  getChartSummaryData,
  selectedKeysToggleKey,
} from 'src/components/Dashboard/helpers/common';
import useFilteredChartData from 'src/components/Dashboard/helpers/useFilteredChartData';
import Loading from 'src/components/Loading';
import { APIConfig } from 'src/config';
import Chart from 'src/enosikit/components/Chart';
import Cards from 'src/enosikit/components/Chart/components/Cards';
import { meterContent, tradeContent } from 'src/enosikit/components/Chart/helpers/cards';
import Pagination from 'src/enosikit/components/Pagination/components/Pagination';
import { getTotalCarbonEmission } from 'src/lib/carbondata/helpers';
import {
  DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE,
  DEFAULT_MAX_VISIBLE_CARDS_COUNT,
  SOURCE, SOURCE_HISTORIAN, SOURCE_TRADES,
  UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from 'src/util/constants';
import { buildOtherSubSeriesLabelGetter } from 'src/util/i18n/constants';
import buildPaginationMetadata from 'src/util/pagination';

import MeterChartTooltip from './ChartTooltip/MeterChartTooltip';
import TradeChartTooltip from './ChartTooltip/TradeChartTooltip';
import {
  buildCardsData, buildChartData,
  buildMeterViewCardLabel, isTooltipTimestampChanged, isValidTooltipTimestamp, tradeViewCardLabel,
} from './helpers/propertyShowChart';

/**
 * Adapter that maps the positional arguments invoked by `enosikit`'s `ChartTooltip`
 * (via the `tooltipLabelFunc` prop on `ChartTrade`) to the props object expected by
 * `TradeChartTooltip`. This isolates the Property-specific trade tooltip UI from the
 * generic chart orchestration logic in `enosikit`, which has no knowledge of trade
 * domain concepts.
 *
 * Defined at module scope (rather than inside the component body) so the function
 * reference is stable across renders, which is required for `React.memo` on
 * `ChartTrade` to avoid unnecessary re-renders.
 * @param {object} data Buy/sell trade tooltip data for the hovered timestamp.
 * @param {string} unitLabel Unit display label.
 * @param {object} chartView Chart aggregation and grouping options.
 * @param {string} unit Chart unit.
 * @returns {React.ReactElement} Rendered `TradeChartTooltip`.
 */
export const renderTradeChartTooltip = (data, unitLabel, chartView, unit) => (
  <TradeChartTooltip
    chartView={chartView}
    data={data}
    unit={unit}
    unitLabel={unitLabel}
  />
);

/**
 * Adapter that maps the positional arguments invoked by `enosikit`'s `ChartTooltip`
 * (via the `tooltipLabelFunc` prop on `ChartMeter`) to the props object expected by
 * `MeterChartTooltip`. This isolates the Property-specific meter tooltip UI from the
 * generic chart orchestration logic in `enosikit`, which has no knowledge of meter
 * domain concepts.
 *
 * Defined at module scope (rather than inside the component body) so the function
 * reference is stable across renders, which is required for `React.memo` on
 * `ChartMeter` to avoid unnecessary re-renders.
 * @param {object} tooltipData Consumed/generated meter tooltip data for the hovered timestamp.
 * @param {string} unitLabel Unit display label.
 * @param {object} chartView Chart aggregation and grouping options.
 * @param {string} unit Chart unit.
 * @returns {React.ReactElement} Rendered `MeterChartTooltip`.
 */
export const renderMeterChartTooltip = (tooltipData, unitLabel, chartView, unit) => (
  <MeterChartTooltip
    chartView={chartView}
    tooltipData={tooltipData}
    unit={unit}
    unitLabel={unitLabel}
  />
);

/**
 * PropertyShowChart component displays charts and cards for property data visualization.
 * Supports both meter view (historian data) and trade view with paginated cards.
 * @param {object} props - Component props.
 * @param {object} props.chartView - Chart view configuration.
 * @param {Function} props.controlSetStateFunc - Function to update control state.
 * @param {Function} props.handleTimespanUpdate - Function to handle
 * timespan updates on chart click.
 * @param {string} props.historianAggregation - Aggregation level for historian data.
 * @param {object} props.mainData - Main data object containing chart and card data.
 * @param {string} [props.noDataMessage] - Message to display when the user cancelled
 * a request and there is no current data available.
 * @param {Function} [props.onChartReadyFunc] - Callback function invoked when the chart
 * has finished its initial render and is ready for interaction.
 * @param {string} props.source - Data source (historian or trades).
 * @param {object} props.timespan - Timespan object with start and finish dates.
 * @param {string} props.timezone - Timezone for date display.
 * @param {string} props.tradeAggregation - Aggregation level for trade data.
 * @param {string} props.unit - Unit of measurement (energy, carbon, or currency).
 * @returns {React.ReactElement} The rendered PropertyShowChart component.
 */
function PropertyShowChart({
  controlSetStateFunc, chartView, historianAggregation, mainData, noDataMessage = null,
  onChartReadyFunc = null, source, timespan, timezone, tradeAggregation, unit,
  handleTimespanUpdate,
}) {
  const intl = useIntl();
  const chartRef = useRef(null);

  const [hoverKeys, setHoverKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState({});
  const tooltipTimestampRef = useRef(null);
  const [chartSize, setChartSize] = useState({ height: 0, width: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const hasCalledOnChartReadyRef = useRef(false);

  const setChartBounds = useCallback(() => {
    if (!chartRef.current) return;

    // Note this excludes padding so ...
    let { clientHeight: height, clientWidth: width } = chartRef.current;
    // ...need to get the computed padding.
    const {
      paddingTop, paddingBottom, paddingLeft, paddingRight,
    } = window.getComputedStyle(chartRef.current);

    if (paddingTop !== '') { height -= parseFloat(paddingTop); }
    if (paddingBottom !== '') { height -= parseFloat(paddingBottom); }
    if (paddingLeft !== '') { width -= parseFloat(paddingLeft); }
    if (paddingRight !== '') { width -= parseFloat(paddingRight); }

    setChartSize({ height, width });
  }, []);

  useEffect(() => {
    // Victory sets the SVG width attribute from the `width` prop. When the
    // actual container width changes (sidebar toggles, CSS transitions, layout
    // shifts) but the prop stays stale, the browser scales the entire SVG to
    // fit — making axis labels and tooltip text appear larger or smaller even
    // though their font-size attribute is unchanged.
    //
    // The previous window 'resize' listener only fired on browser-window
    // resizes, missing container-only changes. ResizeObserver fires whenever
    // the observed element's dimensions change for any reason, keeping the
    // width prop in sync and preventing the SVG scaling mismatch.
    // Falls back to window 'resize' when ResizeObserver is unavailable.
    const node = chartRef.current;
    if (!node) return undefined;

    // Measure immediately on mount so the first render has accurate dimensions.
    setChartBounds();

    if (typeof window.ResizeObserver !== 'function') {
      window.addEventListener('resize', setChartBounds);

      return () => {
        window.removeEventListener('resize', setChartBounds);
      };
    }

    const resizeObserver = new window.ResizeObserver(() => {
      setChartBounds();
    });
    resizeObserver.observe(node);
    return () => resizeObserver.disconnect();
  }, [setChartBounds]);

  const chartViewKey = buildChartViewKey(chartView, source);
  useEffect(() => {
    setCurrentPage(1);
    setHoverKeys([]);
    setSelectedKeys({});
  }, [chartViewKey]);

  const cardsHoverFunc = useCallback((key, hover) => {
    setHoverKeys((prevHoverKeys) => {
      const hasKey = prevHoverKeys.includes(key);
      if (hover && !hasKey) {
        // Add key - need new array
        return [...prevHoverKeys, key];
      }
      if (!hover && hasKey) {
        // Remove key - need new array
        return prevHoverKeys.filter((k) => k !== key);
      }
      // No change - return same reference
      return prevHoverKeys;
    });
  }, []);

  const cardsSelectedFunc = useCallback((key) => {
    setSelectedKeys((prevSelectedKeys) => {
      const updatedKeys = selectedKeysToggleKey(prevSelectedKeys, chartViewKey, key);
      return updatedKeys;
    });
    setHoverKeys((prev) => (prev.length > 0 ? [] : prev));
  }, [chartViewKey]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    setHoverKeys([]);
  }, []);

  const tradesLabel = intl.formatMessage({ id: 'property.property_show.chart.control.source.trades.title', defaultMessage: 'Trades' });
  const meterLabel = intl.formatMessage({ id: 'property.property_show.chart.control.source.historian.title', defaultMessage: 'Meter' });

  const controlOptionFunc = () => {
    const typeOpts = {
      group: SOURCE,
      items: [{
        value: SOURCE_TRADES,
        label: tradesLabel,
        active: (source === SOURCE_TRADES),
        disabled: false,
      }, {
        value: SOURCE_HISTORIAN,
        label: meterLabel,
        active: (source === SOURCE_HISTORIAN),
        disabled: false,
      }],
    };
    return [typeOpts];
  };

  const tooltipUpdateFunc = useCallback((timestamp) => {
    if (!isValidTooltipTimestamp(timestamp)
      || !isTooltipTimestampChanged(tooltipTimestampRef.current, timestamp)) {
      return;
    }
    tooltipTimestampRef.current = timestamp;
  }, []);

  // Use ref to avoid recreating handleChartClick when tooltipTimestamp changes
  const handleChartClick = useCallback(() => {
    if (historianAggregation.match(/^\d+S$/)) {
      return;
    }
    const tooltipTimestamp = tooltipTimestampRef.current;
    if (!isValidTooltipTimestamp(tooltipTimestamp)) {
      return;
    }

    handleTimespanUpdate(tooltipTimestamp, tooltipTimestamp);
  }, [historianAggregation, handleTimespanUpdate]);

  const { width } = chartSize;
  const { aggregateBy } = chartView || {};
  const isByMeter = aggregateBy === DATA_AGGREGATE_BY_METER;
  const metersLength = Object.values(mainData?.meters)?.length || 0;
  const multipleMeter = metersLength > 1;

  const meterView = source === SOURCE_HISTORIAN;
  const tradeView = source === SOURCE_TRADES;

  /**
   * Generates the content for a meter card.
   * @param {object} cardDatum - An object containing the data for the meter card.
   * @param {string} cardDatum.title - The title of the meter.
   * @param {string} cardDatum.identifier - The identifier of the meter.
   * @param {object} cardDatum.buy - The buy object for the meter card.
   * @param {number} cardDatum.buy.volume - The buy volume for the meter card.
   * @param {object} cardDatum.sell - The sell object for the meter card.
   * @param {number} cardDatum.sell.volume - The sell volume for the meter card.
   * @returns {React.ReactElement} - The rendered meter card content.
   */
  const meterViewCard = useCallback((cardDatum) => {
    if (!isByMeter && !meterView) {
      return null;
    }

    const {
      title,
      identifier,
      buy,
      sell,
    } = cardDatum;

    const { volume: buyVolume } = buy || {};
    const { volume: sellVolume } = sell || {};

    const cardLabel = buildMeterViewCardLabel(title, identifier);

    return meterContent(intl, cardLabel, buyVolume, sellVolume);
  }, [intl, isByMeter, meterView]);

  /**
   * Generates the content for a trade card.
   * @param {object} cardDatum - An object containing the data for the trade card.
   * @param {string} cardDatum.title - The property or meter's title.
   * @param {string} cardDatum.identifier - The meter identifier.
   * @param {object} cardDatum.property - The property object.
   * @param {string} cardDatum.label - The trade type or property address.
   * @param {string} cardDatum.subLabel - The counterparty name.
   * @returns {React.ReactElement} - The content for the trade card.
   */
  const tradeViewCard = useCallback((cardDatum) => {
    const { label } = cardDatum;

    const cardsLabel = tradeViewCardLabel(intl, cardDatum, chartView, timezone, timespan);

    return tradeContent(intl, label, cardDatum, cardsLabel);
  }, [intl, chartView, timespan, timezone]);

  const { hasCounterfactual } = mainData || {};

  // Memoize expensive derived data to prevent recalculation on every render
  const chartData = useMemo(
    () => (mainData ? buildChartData(intl, meterView, mainData, timezone, chartView) : null),
    [intl, meterView, mainData, timezone, chartView],
  );

  const chartSummaryData = useMemo(
    () => (mainData ? getChartSummaryData(mainData) : { buy: {}, sell: {} }),
    [mainData],
  );

  const cardsData = useMemo(
    () => buildCardsData(meterView, chartData, chartView),
    [meterView, chartData, chartView],
  );

  const totalCarbonEmission = useMemo(
    () => getTotalCarbonEmission(mainData?.buy?.data),
    [mainData],
  );

  // Reset the onChartReadyFunc flag when mainData changes (new data load)
  const mainDataRef = useRef(mainData);
  useEffect(() => {
    if (mainDataRef.current !== mainData) {
      hasCalledOnChartReadyRef.current = false;
      mainDataRef.current = mainData;
    }
  }, [mainData]);

  // Notify parent that chart is ready after first render with computed data
  useEffect(() => {
    if (chartData && !hasCalledOnChartReadyRef.current) {
      // Use double requestAnimationFrame to ensure paint has completed.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (onChartReadyFunc) {
            onChartReadyFunc();
          }
          hasCalledOnChartReadyRef.current = true;
        });
      });
    }
    return undefined;
  }, [chartData, onChartReadyFunc]);

  const {
    ChartControls, ChartSummary, ChartMeter, ChartTrade,
  } = Chart;

  const {
    pageCount: totalPages,
    currentPage: activePage,
    paginatedItems: pageCards,
  } = useMemo(
    () => buildPaginationMetadata(cardsData, currentPage, DEFAULT_MAX_VISIBLE_CARDS_COUNT),
    [cardsData, currentPage],
  );

  const {
    finalSelectedKeys,
    hasCurrentViewSelectedKeys,
    hasAnySelectedKeys,
    enableClearBtn,
    cardsDataAvailable,
  } = useMemo(
    () => getCardSelectionState(selectedKeys, chartViewKey, cardsData),
    [selectedKeys, chartViewKey, cardsData],
  );

  const meterOtherLabel = intl.formatMessage({
    id: 'property.property_show.chart.cards.other.label',
    defaultMessage: 'Other',
  });

  const getLabelForOtherSubSeriesFunc = useCallback(
    buildOtherSubSeriesLabelGetter(intl),
    [intl],
  );

  const { visibleCards, filteredChartData } = useFilteredChartData({
    pageCards,
    finalSelectedKeys,
    totalPages,
    cardsData,
    chartData,
    meterView,
    meterOtherLabel,
    getLabelForOtherSubSeriesFunc,
  });

  const handleClearCurrentSelection = useCallback(() => {
    setSelectedKeys((prevSelectedKeys) => ({
      ...prevSelectedKeys,
      [chartViewKey]: [],
    }));
  }, [chartViewKey]);

  const handleClearAllSelections = useCallback(() => {
    setSelectedKeys({});
  }, []);

  // All hooks must be called before this guard.
  if (!mainData) {
    return <Loading />;
  }

  return (
    <>
      <div className="mt-4 mb-4 card">
        <div className="card-body" ref={chartRef}>
          <div className="d-flex justify-content-between">
            <div className="flex-fill justify-content-center order-2 order-xs-3 col-xs-12">
              <ChartControls
                buttons={controlOptionFunc()}
                onButtonClick={(opts) => { controlSetStateFunc(opts); }}
              />
            </div>
            <div className="flex-fill order-1 order-xs-1 col-xs-12">
              <ChartSummary
                align="left"
                category="imports"
                hasCounterfactual={hasCounterfactual}
                counterfactual={chartSummaryData.buy.netDiffValue}
                tradedEnergy={chartSummaryData.buy.tradedEnergy}
                tradedValue={chartSummaryData.buy.tradedValue}
                untradedEnergy={chartSummaryData.buy.untradedVolume}
                untradedValue={chartSummaryData.buy.untradedValue}
                rebatedEnergy={chartSummaryData.buy.rebatedEnergy}
                carbonData={totalCarbonEmission}
                mode={APIConfig().MODE}
              />
            </div>
            <div className="flex-fill order-3 order-xs-2 col-xs-12">
              <ChartSummary
                align="right"
                category="exports"
                hasCounterfactual={hasCounterfactual}
                counterfactual={chartSummaryData.sell.netDiffValue}
                tradedEnergy={chartSummaryData.sell.tradedEnergy}
                tradedValue={chartSummaryData.sell.tradedValue}
                untradedEnergy={chartSummaryData.sell.untradedVolume}
                untradedValue={chartSummaryData.sell.untradedValue}
                rebatedEnergy={chartSummaryData.sell.rebatedEnergy}
                mode={APIConfig().MODE}
              />
            </div>
          </div>
          {noDataMessage && (
            <div className="d-flex justify-content-center align-items-center">
              <div className="alert alert-warning">
                {noDataMessage}
              </div>
            </div>
          )}
          {meterView && !noDataMessage && (
            <ChartMeter
              aggregation={historianAggregation}
              chartView={chartView}
              data={filteredChartData}
              handleChartClick={handleChartClick}
              hoverKeys={hoverKeys}
              isByMeter={isByMeter}
              selectedKeys={finalSelectedKeys}
              timespan={timespan}
              timezone={timezone}
              tooltipLabelFunc={renderMeterChartTooltip}
              tooltipUpdateFunc={tooltipUpdateFunc}
              unit={unit}
              width={width}
            />
          )}

          {tradeView && !noDataMessage && (
            <ChartTrade
              aggregation={tradeAggregation}
              chartView={chartView}
              handleChartClick={handleChartClick}
              hoverKeys={hoverKeys}
              multipleMeter={multipleMeter}
              selectedKeys={finalSelectedKeys}
              timespan={timespan}
              timezone={timezone}
              tooltipLabelFunc={renderTradeChartTooltip}
              tooltipUpdateFunc={tooltipUpdateFunc}
              tradeDataBuy={filteredChartData.buy}
              tradeDataSell={filteredChartData.sell}
              unit={unit}
              width={width}
            />
          )}
        </div>
      </div>
      {cardsDataAvailable && (
        <div className="d-flex justify-content-end mb-2">
          <CardSelectionController
            enableClearBtn={enableClearBtn}
            hasCurrentViewSelectedKeys={hasCurrentViewSelectedKeys}
            hasAnySelectedKeys={hasAnySelectedKeys}
            onClearCurrentSelectionFunc={handleClearCurrentSelection}
            onClearAllSelectionsFunc={handleClearAllSelections}
          />
        </div>
      )}
      {(meterView && isByMeter && cardsData.length > 0) && (
        <div className="position-relative">
          <Cards
            cardFunc={meterViewCard}
            data={visibleCards}
            hoverFunc={cardsHoverFunc}
            hoverKeys={hoverKeys}
            selectedFunc={cardsSelectedFunc}
            selectedKeys={finalSelectedKeys}
          />
          {totalPages > 1 && (
            <div className="d-flex justify-content-end mb-4">
              <Pagination
                ariaLabel={intl.formatMessage({ id: 'property.property_show.chart.cards.pagination.navigation.label', defaultMessage: 'Cards pagination navigation' })}
                currentPage={activePage}
                onPageChangeFunc={handlePageChange}
                totalPages={totalPages}
              />
            </div>
          )}
        </div>
      )}
      {tradeView && cardsData.length > 0 && (
        <div className="position-relative">
          <Cards
            cardFunc={tradeViewCard}
            data={visibleCards}
            hoverFunc={cardsHoverFunc}
            hoverKeys={hoverKeys}
            selectedFunc={cardsSelectedFunc}
            selectedKeys={finalSelectedKeys}
          />
          {totalPages > 1 && (
            <div className="d-flex justify-content-end mb-4">
              <Pagination
                ariaLabel={intl.formatMessage({ id: 'property.property_show.chart.cards.pagination.navigation.label', defaultMessage: 'Cards pagination navigation' })}
                currentPage={activePage}
                onPageChangeFunc={handlePageChange}
                totalPages={totalPages}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

PropertyShowChart.propTypes = {
  chartView: PropTypes.shape({
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]).isRequired,
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_PROPERTY,
      DATA_AGGREGATE_BY_METER,
    ]).isRequired,
  }).isRequired,
  controlSetStateFunc: PropTypes.func.isRequired,
  handleTimespanUpdate: PropTypes.func.isRequired,
  historianAggregation: PropTypes.string.isRequired,
  mainData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  noDataMessage: PropTypes.string,
  onChartReadyFunc: PropTypes.func,
  timespan: PropTypes.shape({
    start: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    finish: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  tradeAggregation: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  unit: PropTypes.oneOf([UNIT_ENERGY, UNIT_CARBON, UNIT_CURRENCY]).isRequired,
  source: PropTypes.oneOf([SOURCE_HISTORIAN, SOURCE_TRADES]).isRequired,
};

export default React.memo(PropertyShowChart);
